import type { SubscriptionType } from '@kanbu/schema';
import { Role } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { toast, Button } from '@utima/ui';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { FormPage } from '@/components/page/FormPage';
import { SubscriptionManager } from '@/features/organizations';
import { invalidateCurrentOrganization } from '@/services/invalidateCurrentOrganization';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/subscriptions',
)({
  component: OrganizationsSubscriptionsPage,
  beforeLoad: async ({
    context: { accessGuard },
    params: { organizationId },
  }) => accessGuard([Role.SuperAdmin, Role.Admin], organizationId),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) =>
    trpcUtils.organizations.findOne.ensureData({ id: organizationId }),
});

export function OrganizationsSubscriptionsPage() {
  const { organizationId } = Route.useParams();
  const { t } = useLingui();
  const utils = trpc.useUtils();
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());

  const organizationData = Route.useLoaderData();

  const {
    data: activeSubscription,
    isLoading: isLoadingSubscription,
    isError: isSubscriptionError,
  } = trpc.subscriptions.getActive.useQuery(
    { organizationId },
    {
      refetchOnWindowFocus: true,
      refetchInterval: false,
    },
  );

  const handleSubscriptionUpdate = useCallback(() => {
    utils.organizations.findOne.invalidate({ id: organizationId });
    void invalidateCurrentOrganization(organizationId);
    utils.subscriptions.getActive.invalidate({ organizationId });
    setLastRefreshTime(new Date());
  }, [organizationId, utils]);

  const handleManualRefresh = useCallback(() => {
    toast.info(t`Refreshing subscription data...`);
    handleSubscriptionUpdate();
  }, [handleSubscriptionUpdate, t]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      toast.success(<Trans>Payment Successful</Trans>, {
        description: (
          <Trans>Your payment has been processed successfully.</Trans>
        ),
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      toast.info(<Trans>Payment Canceled</Trans>, {
        description: <Trans>Your payment process was canceled.</Trans>,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (isLoadingSubscription) {
    return <div className='p-4 text-center'>Loading subscription...</div>;
  }

  if (isSubscriptionError) {
    return (
      <div className='text-destructive p-4 text-center'>
        Error loading subscription details.
      </div>
    );
  }

  return (
    <FormPage
      title={`${t`Subscription`} - ${organizationData.name}`}
      breadcrumbs={[
        {
          label: organizationData.name,
          to: '/organizations/$organizationId/payments',
          params: { organizationId },
        },
        {
          label: t`Subscription`,
          to: '/organizations/$organizationId/subscriptions',
          params: { organizationId },
        },
      ]}
    >
      <div className='space-y-4'>
        <div className='flex items-center justify-end gap-2 text-xs text-gray-500'>
          <Trans>Last updated: {lastRefreshTime.toLocaleTimeString()}</Trans>
          <Button
            data-cta='admin-organizations-subscriptions-refresh-button'
            variant='ghost'
            size='icon-sm'
            onClick={handleManualRefresh}
            title={t`Refresh Data`}
          >
            <RefreshCw className='size-3' />
          </Button>
        </div>

        {activeSubscription && (
          <SubscriptionManager
            organization={organizationData}
            activeSubscription={activeSubscription as SubscriptionType}
            onUpdate={handleSubscriptionUpdate}
          />
        )}
      </div>
    </FormPage>
  );
}
